import { ChangeDetectionStrategy, Component, inject, Input, type OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { RatingModule } from 'ngx-bootstrap/rating';
import { FormsModule } from '@angular/forms';
import { RatingStarComponent } from '@ui/sp-nps/rating-star.component';
import { Subject } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

export type NPSFeedback = {
    rating?: number;
    comment?: string;
    sessionNumber?: number;
};

@Component({
    selector: 'sp-nps-modal',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            .modal-container {
                padding: 40px;
                position: relative;

                .modal-title {
                    margin-bottom: 20px;
                    color: var(--base-color);
                }
            }
            .rating {
                color: #ffc107;
                font-size: 35px;
                margin-bottom: 8px;
            }
            .close-btn {
                position: absolute;
                top: 30px;
                right: 36px;
            }

            .content > p {
                margin-bottom: 8px;
            }

            .feedback-input {
                margin-bottom: 20px;
            }
            .feedback-input textarea {
                height: 84px;
            }
        `,
    ],
    template: `
        <div class="modal-container">
            <button type="button" class="close close-btn" aria-label="Close" (click)="close(false)">
                <span aria-hidden="true">&times;</span>
            </button>

            <h2 *ngIf="!isAiAssistant" class="modal-title">{{ 'nps_title' | translate }}</h2>
            <h2 *ngIf="isAiAssistant" class="modal-title">{{ 'nps_title_about_ai' | translate }}</h2>

            <rating
                class="rating"
                [(ngModel)]="rating"
                [max]="5"
                [titles]="['One', 'Two', 'Three', 'Four', 'Five']"
                [customTemplate]="icon"
            ></rating>

            <div class="feedback-input">
                <textarea
                    [(ngModel)]="feedback"
                    class="form-control"
                    name="user-feedback"
                    placeholder="{{ 'nps_placeholder' | translate }}"
                    [attr.maxlength]="500"
                ></textarea>
                <div class="help-block">
                    <small>{{ 500 - feedback.length }}/500 {{ 'characters_left' | translate }}</small>
                </div>
            </div>

            <button
                type="button"
                class="btn btn-success btn-lg w-100"
                [class.disabled]="formInvalid"
                [class.btn-loading]="submitLoading"
                (click)="submit()"
            >
                {{ 'nps_sumbit' | translate }}
            </button>
        </div>

        <!-- Custom rating icon -->
        <ng-template #icon let-i="index" let-v="value">
            <sp-rating-star
                [size]="32"
                [fill]="i < v ? '#ffc73b' : '#dddddd'"
                [stroke]="i < v ? '#ffc73b' : '#dddddd'"
            ></sp-rating-star>
        </ng-template>
    `,
    imports: [CommonModule, RatingModule, FormsModule, RatingStarComponent, TranslateModule],
})
export class NpsModalComponent implements OnInit {
    @Input() isAiAssistant: boolean = false;
    bsModalRef: BsModalRef = inject(BsModalRef);

    onClose$: Subject<NPSFeedback | false> = new Subject<NPSFeedback | false>();
    rating: number | null = null;
    feedback: string = '';
    submitLoading: boolean = false;

    get formInvalid(): boolean {
        return !(this.rating || this.feedback.length);
    }

    ngOnInit(): void {
        console.info('NpsModalComponent');
    }

    submit(): void {
        if (this.formInvalid || this.submitLoading) return;
        this.submitLoading = true;

        let npsPayload = {};

        if (this.rating && this.feedback) {
            npsPayload = {
                rating: this.rating,
                comment: this.feedback,
            };
        } else if (this.rating) {
            npsPayload = {
                rating: this.rating,
            };
        } else {
            npsPayload = {
                comment: this.feedback,
            };
        }

        this.close(npsPayload as NPSFeedback);
    }

    public close(value: NPSFeedback | false): void {
        this.onClose$.next(value);
        this.bsModalRef.hide();
    }
}
